import type React from 'react';

import { action, observable } from 'mobx';

import { ETemplateType } from 'bz-stores/mobx/template-store';

import { EditorState, Entity, Modifier, NestedUtils, RichUtils } from 'tm2-draft-js';

import Control from '../draft/entities/control';
import FormStore from './form-store';
import TemplateStore from './template-store';

interface IHasReportingFields {}

export interface IToolbarValue {
  icon: string;
  key?: string;
  label?: string;
  style?: string | React.CSSProperties;
}

export interface IToolbar {
  icon?: string;
  label?: string;
  mimic?: boolean;
  prefix?: string;
  type: 'composite' | 'block' | 'dropdown' | 'header' | 'snippet';
  value: IToolbarValue[];
}

class DraftStore extends FormStore implements IHasReportingFields {
  @observable mode = 'draft';
  @observable toolbar: IToolbar[] = [];
  @observable control = null;
  @observable created = false;
  @observable reportingFields = [];
  @observable snippets = [];
  @observable flags = [];
  @observable type: ETemplateType | null = null;
  @observable psfs = [];

  reservedFormulaKeys = ['sum', 'avg', 'txtavg', 'datediff', 'checktotal'];

  @action
  load(template, data, type) {
    if (!!type) {
      this.type = type;
    }

    const { ClinicalPSFS, Consultation, Assessment, Modalities, Discharge, Snippet } =
      ETemplateType;
    const psfsTemplates = [ClinicalPSFS, Consultation, Assessment, Modalities, Discharge, Snippet];

    if (!psfsTemplates.includes(this.type)) {
      delete this.controls.PSFS;
    }

    this.dataAsMap.replace(TemplateStore.map(template, data));
    this.editor = TemplateStore.load(template, this.decoratorFn());
    this.loading = false;
    this.updateTemplate();
  }

  @action
  onMode(key) {
    this.mode = key;
    this.onRefresh();
    this.onRegulate();
  }

  @action
  onSchema() {
    let schema = {};
    let filter = (character) => {
      let entityKey = character.getEntity();
      if (!(entityKey > 0)) {
        return false;
      }
      let entity = Entity.get(entityKey);
      let controlKey = entity.getType();
      if (controlKey === 'Group') {
        return false;
      }
      let data = entity.getData();
      if (!data.field) {
        return false;
      }
      let label = (data.label || '').trim();
      let control = controlKey.substring(8);
      schema[data.field] = {
        label: label !== '' ? label : data.field,
        type: this.controls[control].type,
        control: control,
        extraProps: data
      };
    };

    this.editor
      .getCurrentContent()
      .getBlockMap()
      .forEach((block, blockKey) => {
        block.findEntityRanges(filter);
      });

    return schema;
  }

  @action
  onSelect(key, blockKey) {
    let selection = {
      anchorKey: blockKey,
      focusKey: blockKey,
      hasFocus: true,
      isBackward: false
    };
    let content = this.editor.getCurrentContent();
    let block = content.getBlockForKey(blockKey);
    let range = block.findEntityRanges(
      (x) => x.entity === key,
      (start, finish) => {
        selection.focusOffset = !this.highlight ? start : finish;
        selection.anchorOffset = start;
      }
    );

    selection = this.editor.getSelection().merge(selection);

    if (this.highlight) {
      this.editor = this.applyHighlight(selection);
      return;
    }

    this.control = key;
    this.editor = EditorState.acceptSelection(this.editor, selection);
  }

  @action
  insertControl(key, selection) {
    let { editor } = this;
    let content = editor.getCurrentContent();

    let entity = Modifier.insertText(content, selection, '|', null, key);

    editor = EditorState.push(editor, entity, 'insert-text');

    let offset = selection.getFocusOffset();

    let updated = selection.merge({
      focusOffset: offset + 1,
      anchorOffset: offset + 1
    });

    let attributes = {};

    if (
      Entity.get(key).getType() !== 'Control-Label' &&
      Entity.get(key).getType() !== 'Control-DateTimePicker'
    ) {
      attributes.inline = offset > 0;
    }

    Entity.mergeData(key, attributes);

    updated = updated.set('hasFocus', false);
    editor = EditorState.acceptSelection(editor, updated);

    this.editor = editor;
    this.control = key;
    this.created = true;
    this.updateTemplate();
  }

  @action
  moveControl(entityKey, selection) {
    let content = this.editor.getCurrentContent();
    let source = this.editor.getSelection();

    content.getBlocksAsArray().map((block) => {
      block.findEntityRanges(
        (x) => {
          return x.getEntity() === entityKey;
        },
        (start, end) => {
          source = source.merge({
            focusKey: block.key,
            anchorKey: block.key,
            focusOffset: end,
            anchorOffset: start
          });
        }
      );
    });

    if (source.key === selection.key && source.focusOffset < selection.focusOffset) {
      let offset = selection.getFocusOffset();
      selection = this.editor.getSelection().merge({
        focusOffset: offset - 1,
        anchorOffset: offset - 1
      });
    }

    content = Modifier.moveText(content, source, selection);

    this.editor = EditorState.push(this.editor, content, 'insert-text');

    selection = this.editor.getSelection().merge({
      focusOffset: offset + 2,
      anchorOffset: offset + 2
    });

    this.editor = EditorState.forceSelection(this.editor, selection);
  }

  @action
  insertGroup(entityKey, selection) {
    let { editor } = this;
    let content = editor.getCurrentContent();

    content = Modifier.splitBlock(content, selection);
    content = Modifier.splitBlock(content, selection);

    editor = EditorState.push(editor, content, 'insert-text');

    let res = NestedUtils.toggleBlockType(editor, 'header-one', this.blockRenderMap);
    editor = RichUtils.toggleBlockType(res.editorState, 'group-container');

    let key = editor.getSelection().getAnchorKey();

    let ol = NestedUtils.toggleBlockType(editor, 'header-one', this.blockRenderMap);
    editor = RichUtils.toggleBlockType(ol.editorState, 'group-item');

    selection = selection.merge({
      anchorKey: key,
      anchorOffset: 0,
      focusKey: key,
      focusOffset: 0
    });

    content = Modifier.insertText(editor.getCurrentContent(), selection, '\r', null, entityKey);
    this.editor = EditorState.push(editor, content, 'insert-text');
    this.control = entityKey;
    this.created = true;
  }

  @action
  moveGroup(entityKey, selection) {
    return;
    //let content = this.editor.getCurrentContent()

    //let blocks = []

    // content.getBlocksAsArray().map((block) => {
    //   block.findEntityRanges(
    //     ((x) => { return x.getEntity() === entityKey}),
    //     ((start, end) => { blocks.push(block) })
    //   )
    // })

    //let blockMap = ContentState.createFromBlockArray(blocks).getBlockMap()

    // let remove = this.editor.getSelection().merge({
    //   focusKey: blocks[0].getKey(),
    //   anchorKey: blocks[0].getKey(),
    //   focusOffset: blocks[0].getLength(),
    //   anchorOffset: 0
    // })
    //
    // content = Modifier.removeRange(content, remove)
    // content = Modifier.replaceWithFragment(content, selection, this.createTestGroup())
    // this.editor = EditorState.push(this.editor, content, 'insert-fragment')
  }

  @action
  onDragStart(key, e) {
    if (this.highlight) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.dataTransfer.setData('text', key);
    e.dataTransfer.setData('move', key);
  }
  controlFn(props) {
    return (
      <Control
        value={this.dataAsMap.get(props.field)}
        valueOther={this.dataAsMap.get(props.field + '-explanation')}
        errored={!!this.errors[props.field]}
        warning={props.attributes.required === true}
        lists={this.lists}
        cache={this.cache}
        lookups={this.lookups}
        searches={this.searches}
        readOnly={this.readOnly}
        draggable={this.mode === 'draft'}
        control={props.control}
        children={props.children}
        attributes={props.attributes}
        inline={props.inline || false}
        onAction={this.onAction.bind(this)}
        onSelect={this.onSelect.bind(this, props.key, props.blockKey)}
        onChange={this.onChange.bind(this, props.field)}
        onDragStart={this.onDragStart.bind(this, props.key)}
        onChangeOther={this.onChange.bind(this, props.field + '-explanation')}
        onConnect={this.onConnect}
      />
    );
  }
}

export default DraftStore;
